import React, {useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';

import {dataManager} from "../../managers/DataManager";
import {flowManager} from "../../managers/FlowManager";
import {userManager} from "../../managers/UserManager";
import {dataKeys} from "../../enums/dataKeys";
import {useOrders} from "@shared/hooks/useOrders";
import {urlToPage} from "@shared/lib/urlToPage";
import {StepHeader} from "../../components/StepHeader";
import {StepCategory} from "../../components/StepCategory";
import {TileCheckbox} from "../../components/TileCheckbox";
import {MainCategoryTile} from "./components/MainCategoryTile";
import {Picture} from "../../components/Picture";

const HOMEY_MODEL_LABELS = {
  homey1s: 'homey-early-2016',
  homey2s: 'homey-early-2018',
  homey3s: 'homey-early-2019',
  homey3d: 'homey-pro-early-2019',
  homey4d: 'homey-pro-early-2019',
  homey5q: 'homey-pro-early-2023',
  homey6q: 'homey-pro-mini',
  homey7q: 'homey-pro-2026',
  shs: 'homey-self-hosted-server'
};

MainCategoryStep.propTypes = {
  index: PropTypes.number
}

export function MainCategoryStep(props) {
  const name = MainCategoryStep.name;
  const data = dataManager.store(state => state.data); // Data is updated on state change.
  const steps = flowManager.store(state => state.steps); // Steps is updated on state change.
  const user = userManager.store(state => state.user); // User is updated on state change.
  const stepRef = useRef(null);
  const [checkValue, setCheckValue] = useState("");
  const [orders, isLoading] = useOrders();
  const options = {};

  const homeyImage = "/img/pages/support-form/main-category/homey.png";
  const homeyBridgeImage = "/img/pages/support-form/main-category/homey-bridge.png";
  const homeyPro2019Image = "/img/pages/support-form/main-category/homey-pro-2019.png";
  const homeyPro2023Image = "/img/pages/support-form/main-category/homey-pro-2023.png";
  const homeyProMiniImage = "/img/pages/support-form/main-category/homey-pro-mini.png";
  const homeySelfHostedServerImage = "/img/pages/support-form/main-category/homey-self-hosted-server.png";
  const homeyUnlistedImage = "/img/pages/support-form/main-category/homey-unlisted.png";

  const orderImage = "/img/pages/support-form/main-category/order.png";
  const orderUnlistedImage = "/img/pages/support-form/main-category/order-unlisted.png";

  const backupsImage = "/img/pages/support-form/main-category/backups.png";
  const developerImage = "/img/pages/support-form/main-category/developer.png";
  const premiumImage = "/img/pages/support-form/main-category/premium.png";
  const selfHostedServerMonthlyImage = "/img/pages/support-form/main-category/self-hosted-server-monthly.png";
  const selfHostedServerLifetimeImage = "/img/pages/support-form/main-category/self-hosted-server-lifetime.png";
  const subscriptionUnlistedImage = "/img/pages/support-form/main-category/subscription-unlisted.png";

  // On mount, focus the step and scroll into view.
  useEffect(() => {
    stepRef.current.focus({ preventScroll: true });
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, [])

  useEffect(() => {
    const entry = data.find(item => item.name === name);
    if (entry) {
      setCheckValue(entry.value);
    }
  }, [data, name])

  function handleSelect(event) {
    const value = event.target.value;
    const data = options[value].data;
    const action = data.action;
    const entry = { name, value, ...data };

    flowManager.nextActionStep(props.index, entry, action);
  }

  function getFormattedDate(date) {
    const newDate = new Date(date);
    return newDate.toLocaleDateString();
  }

  function getAttributesByHomey(homey) {
    if (homey.platform === 'cloud') {
      return {
        subtitle: homey.bridges.length > 0 ? "With Homey Bridge" : "Without Homey Bridge",
        image: homey.bridges.length > 0 ? homeyBridgeImage : homeyImage,
        alt: homey.bridges.length > 0 ? "Homey Bridge" : "Homey",
        data: {
          ...MainCategoryStep.homey.cloud,
          [dataKeys.labels]: ["homey-cloud"],
          [dataKeys.homeyApps]: homey.apps,
          [dataKeys.homeyBridges]: homey.bridges.length,
          [dataKeys.homeyId]: homey.id,
          [dataKeys.homeyLanguage]: homey.language,
          [dataKeys.homeyLicenses]: homey.licenses.join(", "),
          [dataKeys.homeyPlatform]: homey.platform,
          [dataKeys.homeyRegion]: homey.region,
          [dataKeys.homeyUsers]: homey.users.length
        }
      }
    }
    if (homey.platform === 'local') {
      switch (homey.model) {
        // Homey Self-Hosted Server
        case 'shs':
          return {
            subtitle: homey.modelName,
            image: getHomeyImage(homey.model),
            alt: "Homey Self-Hosted Server",
            data: {
              ...MainCategoryStep.homey.shs,
              [dataKeys.labels]: [HOMEY_MODEL_LABELS[homey.model]],
              [dataKeys.homeyApps]: homey.apps,
              [dataKeys.homeyBridges]: homey.bridges.length,
              [dataKeys.homeyId]: homey.id,
              [dataKeys.homeyLanguage]: homey.language,
              [dataKeys.homeyLicenses]: homey.licenses.join(", "),
              [dataKeys.homeyModel]: homey.model,
              [dataKeys.homeyModelName]: homey.modelName,
              [dataKeys.homeyPlatform]: homey.platform,
              [dataKeys.homeyRegion]: homey.region,
              [dataKeys.homeySoftwareVersion]: homey.softwareVersion,
              [dataKeys.homeyUsers]: homey.users.length
            }
          }
        // Homey Pro mini
        case 'homey6q':
          return {
            subtitle: homey.modelName,
            image: getHomeyImage(homey.model),
            alt: "Homey Pro mini",
            data: {
              ...MainCategoryStep.homey.proMini,
              [dataKeys.labels]: [HOMEY_MODEL_LABELS[homey.model]],
              [dataKeys.homeyApps]: homey.apps,
              [dataKeys.homeyBridges]: homey.bridges.length,
              [dataKeys.homeyId]: homey.id,
              [dataKeys.homeyLanguage]: homey.language,
              [dataKeys.homeyLicenses]: homey.licenses.join(", "),
              [dataKeys.homeyModel]: homey.model,
              [dataKeys.homeyModelName]: homey.modelName,
              [dataKeys.homeyPlatform]: homey.platform,
              [dataKeys.homeyRegion]: homey.region,
              [dataKeys.homeySoftwareVersion]: homey.softwareVersion,
              [dataKeys.homeyUsers]: homey.users.length
            }
          }
        // Homey Pro
        default:
          return {
            subtitle: homey.modelName,
            image: getHomeyImage(homey.model),
            alt: "Homey Pro",
            data: {
              ...MainCategoryStep.homey.pro,
              [dataKeys.labels]: [HOMEY_MODEL_LABELS[homey.model]],
              [dataKeys.homeyApps]: homey.apps,
              [dataKeys.homeyBridges]: homey.bridges.length,
              [dataKeys.homeyId]: homey.id,
              [dataKeys.homeyLanguage]: homey.language,
              [dataKeys.homeyLicenses]: homey.licenses.join(", "),
              [dataKeys.homeyModel]: homey.model,
              [dataKeys.homeyModelName]: homey.modelName,
              [dataKeys.homeyPlatform]: homey.platform,
              [dataKeys.homeyRegion]: homey.region,
              [dataKeys.homeySoftwareVersion]: homey.softwareVersion,
              [dataKeys.homeyUsers]: homey.users.length
            }
          }
      }
    }
  }

  function getHomeyImage(model) { // TODO: Check other models
    switch (model) {
      case "homey5q":
      case "homey7q":
        return homeyPro2023Image;
      case "homey6q":
        return homeyProMiniImage;
      case "shs":
        return homeySelfHostedServerImage;
      case "homey3d":
      default:
        return homeyPro2019Image;
    }
  }

  function getAttributesBySubscriptionId(subscriptionId) {
    if (subscriptionId.startsWith('homey_plus_')) {
      return {
        title: "Homey Pro Backups",
        image: backupsImage,
        alt: "Homey Pro Backups subscription card",
        data: {
          ...MainCategoryStep.subscriptions.backups,
          [dataKeys.subscriptionId]: subscriptionId,
          [dataKeys.resolutionLinks]: [{ name: "Manage Subscriptions", url: urlToPage("account/subscriptions") }]
        }
      };
    }
    if (subscriptionId.startsWith('homey_premium_')) {
      return {
        title: "Homey Premium",
        image: premiumImage,
        alt: "Homey Premium subscription card",
        data: {
          ...MainCategoryStep.subscriptions.premium,
          [dataKeys.subscriptionId]: subscriptionId,
          [dataKeys.resolutionLinks]: [{ name: "Manage Subscriptions", url: urlToPage("account/subscriptions") }]
        }
      };
    }
    if (subscriptionId.startsWith('homey_developer_')) {
      return {
        title: "Homey Developer",
        image: developerImage,
        alt: "Homey Developer subscription card",
        data: {
          ...MainCategoryStep.subscriptions.developer,
          [dataKeys.subscriptionId]: subscriptionId,
          [dataKeys.resolutionLinks]: [{ name: "Manage Subscriptions", url: urlToPage("account/subscriptions") }]
        }
      };
    }
    if (subscriptionId.startsWith('homey_self_hosted_server_monthly')) {
      return {
        subtitle: "Monthly Subscription",
        image: selfHostedServerMonthlyImage,
        alt: "Homey Self-Hosted Server Monthly subscription card",
        data: {
          ...MainCategoryStep.subscriptions.selfHostedServerMonthly,
          [dataKeys.subscriptionId]: subscriptionId,
          [dataKeys.resolutionLinks]: [{
            name: "Manage Homey Self-Hosted Servers",
            url: urlToPage("account/self-hosted-servers")
          }]
        }
      };
    }
    if (subscriptionId.startsWith('homey_self_hosted_server_lifetime')) {
      return {
        subtitle: "Lifetime License",
        image: selfHostedServerLifetimeImage,
        alt: "Homey Self-Hosted Server Lifetime License card",
        data: {
          ...MainCategoryStep.subscriptions.selfHostedServerLifetime,
          [dataKeys.subscriptionId]: subscriptionId,
          [dataKeys.resolutionLinks]: [{
            name: "Manage Homey Self-Hosted Servers",
            url: urlToPage("account/self-hosted-servers")
          }]
        }
      };
    }
    return {
      title: subscriptionId,
      image: subscriptionUnlistedImage,
      alt: "Blank subscription card",
      data: {
        ...MainCategoryStep.subscriptions.notListed,
        [dataKeys.subscriptionId]: subscriptionId,
        [dataKeys.resolutionLinks]: [{ name: "Manage Subscriptions", url: urlToPage("account/subscriptions") }]
      }
    };
  }

  function addImage(image, alt, type) {
    return <Picture className={`support-form-main-category-tile__image--${type}`} src={image} alt={alt}/>
  }


  // Account
  options[`account-0`] = {
    title: user.fullname,
    subtitle: user.email,
    image: <img className="support-form-main-category-tile__image--round" src={user.avatar.medium}
                srcSet={`${user.avatar.large} 2x`} alt={"Account avatar"}/>,
    data: { ...MainCategoryStep.account }
  };

  // Homeys
  let homeyIndex = 0;
  user?.homeys?.forEach((homey) => {
    const attributes = getAttributesByHomey(homey);
    options[`homey-${homeyIndex}`] = {
      title: homey.name,
      subtitle: attributes.subtitle,
      image: addImage(attributes.image, attributes.alt, "full"),
      data: { ...attributes.data }
    };
    homeyIndex++;
  });

  options[`homey-${homeyIndex}`] = {
    text: "My Homey\nisn’t listed",
    image: addImage(homeyUnlistedImage, "Blank Homey image", "full"),
    data: { ...MainCategoryStep.homey.notListed }
  };

  // Orders
  let orderIndex = 0;
  orders?.forEach((order) => {
    options[`order-${orderIndex}`] = {
      title: `Order ${order.id}`,
      subtitle: getFormattedDate(order.date),
      image: addImage(orderImage, "Shopping bag image", "full"),
      data: {
        ...MainCategoryStep.orders.order,
        [dataKeys.orderId]: order.id,
        [dataKeys.path]: ["My Orders", "Order"],
        [dataKeys.resolutionLinks]: [{ name: "Manage order in My Account", url: urlToPage("account/orders") }]
      }
    };
    orderIndex++;
  });
  options[`order-${orderIndex}`] = {
    text: "My order\nisn’t listed",
    image: addImage(orderUnlistedImage, "Blank shopping bag image", "full"),
    data: { ...MainCategoryStep.orders.notListed }
  };

  // Subscriptions
  let subscriptionIndex = 0;
  Object.entries(user.subscriptions).forEach(([subscriptionId, subscription]) => {
    if (subscriptionId.startsWith('homey_self_hosted_server_')) return; // Handled separately below
    const attributes = getAttributesBySubscriptionId(subscriptionId);
    options[`subscription-${subscriptionIndex}`] = {
      title: attributes.title,
      subtitle: `Since ${getFormattedDate(subscription.startedAt)}`,
      image: addImage(attributes.image, attributes.alt, "full"),
      data: { ...attributes.data }
    };
    subscriptionIndex++;
  })
  // Self-Hosted Server subscriptions from Homeys
  user?.homeys?.forEach((homey) => {
    if (homey.model === 'shs' && ['monthly', 'lifetime'].includes(homey.selfHostedServerSubscriptionType)) {
      const attributes = getAttributesBySubscriptionId(`homey_self_hosted_server_${homey.selfHostedServerSubscriptionType}`);
      options[`subscription-${subscriptionIndex}`] = {
        title: homey.name,
        subtitle: attributes.subtitle,
        image: addImage(attributes.image, attributes.alt, "full"),
        data: { ...attributes.data }
      };
      subscriptionIndex++;
    }
  })
  options[`subscription-${subscriptionIndex}`] = {
    text: "My subscription\nisn’t listed",
    image: addImage(subscriptionUnlistedImage, "Blank subscription image", "full"),
    data: { ...MainCategoryStep.subscriptions.notListed }
  };

  return (
    <>
      <div ref={stepRef} className="support-form-step">
        <StepHeader
          index={props.index}
          question="What’s your question about?"
          description="We’d love to help you out! First we need to know what your question is about."
          isLoading={isLoading}
        />
        <div className="support-form-step__categories">
          <StepCategory heading="My Account" description="Your name, password, 2FA and more.">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("account") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <MainCategoryTile title={option.title} subtitle={option.subtitle} image={option.image}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory
            heading="My Homeys"
            description="Get help with Devices, Flows, Apps, Insights, Voice Assistants and more.">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("homey") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <MainCategoryTile title={option.title} subtitle={option.subtitle} text={option.text}
                                    image={option.image}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="My Orders" description="Your order’s status, invoices and returns.">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("order") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <MainCategoryTile title={option.title} subtitle={option.subtitle} text={option.text}
                                    image={option.image}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="My Subscriptions" description="Help with billing, renewal and cancellation.">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("subscription") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <MainCategoryTile title={option.title} subtitle={option.subtitle} text={option.text}
                                    image={option.image}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
        </div>
      </div>
      {steps.length === props.index + 1 && (
        <div className="support-form-step">
          <StepHeader
            index={props.index + 1}
            question="Select one of the above options."
          />
        </div>
      )}
    </>
  );
}


MainCategoryStep.account = {
  action: "account",
  [dataKeys.labels]: ["account"],
  [dataKeys.path]: ["My Account"]
}
MainCategoryStep.homey = {
  cloud: {
    action: "cloud",
    [dataKeys.path]: ["My Homeys", "Homey Cloud"]
  },
  pro: {
    action: "pro",
    [dataKeys.path]: ["My Homeys", "Homey Pro"]
  },
  proMini: {
    action: "proMini",
    [dataKeys.path]: ["My Homeys", "Homey Pro mini"]
  },
  shs: {
    action: "shs",
    [dataKeys.path]: ["My Homeys", "Homey Self-Hosted Server"]
  },
  notListed: {
    action: "homeyNotListed",
    [dataKeys.labels]: ["homey-not-listed"],
    [dataKeys.path]: ["My Homeys", "Unlisted"]
  }
}
MainCategoryStep.orders = {
  order: {
    action: "order",
    [dataKeys.labels]: ["order"]
  },
  notListed: {
    action: "orderNotListed",
    [dataKeys.labels]: ["order-not-listed"],
    [dataKeys.path]: ["My Orders", "Unlisted"]
  }
}
MainCategoryStep.subscriptions = {
  backups: {
    action: "subscriptionBackups",
    [dataKeys.labels]: ["subscription", "homey-pro-backups"],
    [dataKeys.path]: ["My Subscriptions", "Homey Pro Backups"]
  },
  developer: {
    action: "subscriptionDeveloper",
    [dataKeys.labels]: ["subscription", "homey-developer"],
    [dataKeys.path]: ["My Subscriptions", "Homey Developer"]
  },
  premium: {
    action: "subscriptionPremium",
    [dataKeys.labels]: ["subscription", "homey-premium"],
    [dataKeys.path]: ["My Subscriptions", "Homey Premium"]
  },
  selfHostedServerMonthly: {
    action: "subscriptionSelfHostedServerMonthly",
    [dataKeys.labels]: ["subscription", "homey-self-hosted-server", "monthly-subscription"],
    [dataKeys.path]: ["My Subscriptions", "Homey Self-Hosted Server"]
  },
  selfHostedServerLifetime: {
    action: "subscriptionSelfHostedServerLifetime",
    [dataKeys.labels]: ["subscription", "homey-self-hosted-server", "lifetime-license"],
    [dataKeys.path]: ["My Subscriptions", "Homey Self-Hosted Server"]
  },
  notListed: {
    action: "subscriptionNotListed",
    [dataKeys.labels]: ["subscription-not-listed"],
    [dataKeys.path]: ["My Subscriptions", "Unlisted"]
  }
}
